import { extendTheme } from '@chakra-ui/react';
import { createTheme } from '@mui/material/styles';
import {
  typography,
  sizing,
  radii,
  shadows,
  borders,
  motion,
  semanticColors,
  componentSizing,
} from './tokens';

export const chakraExtendedTheme = extendTheme({
  fonts: {
    heading: `"Plus Jakarta Sans", sans-serif;`,
    body: `"Plus Jakarta Sans", sans-serif;`,
  },
});

declare module '@mui/material/styles' {
  interface Theme {
    sizes: {
      avatar: {
        xs: number;
        sm: number;
        md: number;
        lg: number;
      };
    };
  }

  interface ThemeOptions {
    sizes?: {
      avatar?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
      };
    };
  }

  interface Transitions {
    hover: string;
  }

  interface TransitionsOptions {
    hover?: string;
  }

  interface Palette {
    auxRed: Palette['primary'];
    auxGreen: Palette['primary'];
    pageBackground: Palette['primary'];
  }

  interface PaletteOptions {
    auxRed?: PaletteOptions['primary'];
    auxGreen?: PaletteOptions['primary'];
    pageBackground?: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    auxRed: true;
    auxGreen: true;
  }
}

// ---------------------------------------------------------------------------
// Trio UI Theme (styled-components) — Base Web aligned
// ---------------------------------------------------------------------------
export const trioUITheme = {
  breakpoints: {
    base: '0em',
    '2xl': '1920px',
    lg: '62em',
    md: '48em',
    sm: '30em',
    xl: '75em',
  },
  colors: {
    'primary-90': '#001b4b',
    'primary-80': '#043387',
    'primary-70': '#043eaf',
    'primary-60': '#054ad9',
    'primary-50': '#376EE2',
    'primary-40': '#6A92E9',
    'primary-30': '#CDDBF6',
    'secondary-60': '#a8bff2',
    'secondary-30': '#dce5fa',
    'secondary-20': '#eef2fc',
    'tertiary-60': '#ffd166',
    'tertiary-50': '#ffda85',
    'light-red': '#ff5e54',
    'dark-cyan': '#0ab0a1',
    'neutral-0': '#ffffff',
    'neutral-10': '#fafafa',
    'neutral-20': '#f4f4f5',
    'neutral-30': '#e4e4e7',
    'neutral-40': '#d9d9d9',
    'neutral-50': '#C5C5C7',
    'neutral-60': '#96979b',
    'neutral-70': '#71717A',
    'neutral-80': '#4a4b4e',
    'neutral-90': '#202123',
    'neutral-100': '#09090B',
    warning: '#EF5B5B',
    success: '#2AD062',
    'purple-50': '#655CC1',
    black: '#09090B',
  },
  typography,
  sizing,
  radii,
  shadows,
  borders,
  motion,
  semanticColors,
  componentSizing,

  // Backward compat aliases
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '36px',
    '2xl': '48px',
    '3xl': '64px',
  },
};

// ---------------------------------------------------------------------------
// MUI Theme — Base Web aligned typography
// ---------------------------------------------------------------------------
export const defaultTheme = {
  palette: {
    primary: {
      main: '#054AD9',
      light: '#A8BFF2',
    },
    secondary: {
      main: '#FFD166',
    },
    auxRed: {
      main: '#FF5E54',
    },
    auxGreen: {
      main: '#0AB0A1',
    },
    error: {
      main: '#FF5E54',
    },
    pageBackground: {
      main: '#FFFFFF',
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
  },
  typography: {
    fontFamily: ['Plus Jakarta Sans', 'Helvetica', 'Arial', 'sans-serif'].join(','),
    h1: {
      ...typography.DisplayMedium,
      '@media (max-width:960px)': { fontSize: '40px', lineHeight: '52px' },
      '@media (max-width:600px)': { fontSize: '36px', lineHeight: '44px' },
    },
    h2: {
      ...typography.HeadingXLarge,
      '@media (max-width:960px)': { fontSize: '32px', lineHeight: '40px' },
      '@media (max-width:600px)': { fontSize: '28px', lineHeight: '36px' },
    },
    h3: { ...typography.HeadingSmall },
    h4: { ...typography.LabelLarge },
    h5: { ...typography.LabelMedium },
    h6: { ...typography.LabelSmall },
    body1: { ...typography.ParagraphMedium },
    body2: { ...typography.ParagraphSmall },
    button: {
      ...typography.LabelMedium,
      textTransform: 'none' as const,
    },
    caption: { ...typography.ParagraphXSmall },
    overline: {
      ...typography.LabelXSmall,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
    },
    subtitle1: {
      ...typography.LabelSmall,
      color: semanticColors.contentTertiary,
    },
    subtitle2: {
      ...typography.ParagraphSmall,
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
  sizes: {
    avatar: {
      xs: 40,
      sm: 56,
      md: 80,
      lg: 120,
    },
  },
  transitions: {
    hover: motion.duration200,
  },
};

const stylingTheme = createTheme(defaultTheme);

// ---------------------------------------------------------------------------
// MUI Component Overrides — Base Web aligned
// ---------------------------------------------------------------------------
const overridingTheme = createTheme(stylingTheme, {
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          transition: `background-color ${motion.duration200} ${motion.easeDefault}`,
          '&:hover': { boxShadow: 'none' },
        },
        sizeSmall: {
          minHeight: 36,
          padding: `${componentSizing.button.compact.paddingV} ${componentSizing.button.compact.paddingH}`,
          fontSize: componentSizing.button.compact.fontSize,
          lineHeight: componentSizing.button.compact.lineHeight,
        },
        sizeMedium: {
          minHeight: 48,
          padding: `${componentSizing.button.default.paddingV} ${componentSizing.button.default.paddingH}`,
          fontSize: componentSizing.button.default.fontSize,
          lineHeight: componentSizing.button.default.lineHeight,
        },
        sizeLarge: {
          minHeight: 56,
          padding: `${componentSizing.button.large.paddingV} ${componentSizing.button.large.paddingH}`,
          fontSize: componentSizing.button.large.fontSize,
          lineHeight: componentSizing.button.large.lineHeight,
        },
        contained: {
          '&:hover': {
            boxShadow: 'inset 999px 999px 0px rgba(255, 255, 255, 0.1)',
          },
        },
        outlined: {
          '&:hover': {
            boxShadow: 'inset 999px 999px 0px rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: 'none',
          border: `2px solid ${semanticColors.borderOpaque}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: shadows.shadow400,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: semanticColors.inputFill,
            transition: `background-color ${motion.duration200} ${motion.easeDefault}, border-color ${motion.duration200} ${motion.easeDefault}`,
            '& fieldset': {
              borderColor: 'transparent',
              borderWidth: 2,
            },
            '&:hover fieldset': {
              borderColor: 'transparent',
            },
            '&.Mui-focused fieldset': {
              borderWidth: 2,
              borderColor: semanticColors.borderSelected,
            },
            '&.Mui-focused': {
              backgroundColor: semanticColors.inputFillActive,
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: semanticColors.inputFill,
          transition: `background-color ${motion.duration200} ${motion.easeDefault}, border-color ${motion.duration200} ${motion.easeDefault}`,
          '& fieldset': {
            borderColor: 'transparent',
            borderWidth: 2,
          },
          '&:hover fieldset': {
            borderColor: 'transparent',
          },
          '&.Mui-focused fieldset': {
            borderWidth: 2,
            borderColor: semanticColors.borderSelected,
          },
          '&.Mui-focused': {
            backgroundColor: semanticColors.inputFillActive,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          fontWeight: 500,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-selected': {
            backgroundColor: stylingTheme.palette.primary.dark,
            '&:hover': {
              backgroundColor: stylingTheme.palette.primary.dark,
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: shadows.shadow700,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

overridingTheme.shadows[24] =
  '0px 11px 15px -7px rgba(0,0,0,0.08),0px 24px 38px 3px rgba(0,0,0,0.08),0px 9px 46px 8px rgba(0,0,0,0.08)';

const theme = overridingTheme;

export default theme;

export const styledComponentsTheme = defaultTheme;
